import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "../data/content";
import { scrollToId } from "../lib/scroll";

const TITLE_LINES = ["Santa Caterina", "d'Alessandria"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative h-[100svh] min-h-[600px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img
          src={IMAGES.heroFacade}
          alt="Facciata di una basilica romanico-gotica in pietra leccese"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/35 to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24 sm:pb-28"
      >
        <motion.div
          className="flex items-center gap-3 mb-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="diamond" aria-hidden="true" />
          <span className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-goldlight">
            Galatina · Lecce · Puglia — Secolo XIV
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-serif font-semibold tracking-tight leading-[0.95] text-ivory text-4xl sm:text-6xl lg:text-7xl"
        >
          {TITLE_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1.1,
                  delay: 0.5 + i * 0.16,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {i === 1 ? <em className="text-goldlight not-italic font-medium">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          className="mt-6 max-w-xl text-base sm:text-lg text-ivory/85 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
        >
          La Basilica che custodisce secoli di arte, storia e fede nel cuore di
          Galatina
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            onClick={() => scrollToId("storia")}
            data-testid="hero-cta-button"
            className="group relative inline-flex items-center gap-3 bg-gold text-ink font-semibold text-sm sm:text-base tracking-wide uppercase px-8 py-4 overflow-hidden transition-colors duration-500 hover:bg-goldlight rounded-full"
          >
            Esplora la Basilica
            <ChevronDown
              size={18}
              className="transition-transform duration-500 group-hover:translate-y-1"
            />
          </button>
          <button
            onClick={() => scrollToId("galleria")}
            data-testid="hero-gallery-button"
            className="text-sm sm:text-base font-medium tracking-wide uppercase text-ivory/85 hover:text-goldlight border-b border-ivory/40 hover:border-goldlight pb-1 transition-colors duration-300"
          >
            Vai alla galleria
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-transparent via-goldlight to-transparent"
        />
      </motion.div>
    </section>
  );
};
